import { ButtonSharedScreen } from "@kalyzee/escaet-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MediaStreamSilentTrack, setWorkerInterval, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { toastError } from "../../helpers/toast";
import { useDynamicMedia, useMeetingSelector } from "../../hooks/meeting";
import { getAudioContextForVolumeAnalyzer } from "../../helpers/audio";

export const ScreenMediaName = "screen";

export type ScreenShareStatus = "idle" | "starting" | "ongoing" | "stopping";

const DISPLAY_MEDIA_CONSTRAINTS: DisplayMediaStreamOptions = {
  video: {width: {max: 1920}, height: {max: 1080}, frameRate: {ideal: 30}},
  audio:  {echoCancellation: {ideal: false}, autoGainControl: {ideal: false}, noiseSuppression: {ideal: false}},
}

const SCREENSHARE_FPS = 20;

export interface ScreenShareImperativeAPI {
  stop: () => Promise<void>;
}

export interface ScreenShareProps {
  meetingContainerAPI: MeetingContainerAPI | undefined;
  onStatus?: (status: ScreenShareStatus) => void;
  onStream?: (stream: MediaStream) => void;
}

class CanvasWrapper {
  private backgroundTask: { clear: () => void } | undefined;

  private inputStream?: MediaStream;

  private data?: {
    canvas: HTMLCanvasElement;
    video: HTMLVideoElement;
    canvasStream: MediaStream;
    renderingCtx: CanvasRenderingContext2D;
  };
  
  constructor() {}

  init() {
    if (this.data) {
      this.cleanup();
    }

    const canvas = document.createElement("canvas");
    const renderingCtx = canvas.getContext("2d")!;
    const canvasStream = canvas.captureStream();

    const video = document.createElement("video");

    this.data = {
      canvas,
      video,
      canvasStream,
      renderingCtx,
    };

    return this.data;
  }

  setInputStream(stream: MediaStream) {
    if (!this.data) {
      console.error("attempting to set input stream before init");
      return;
    }

    this.inputStream = stream;

    this.data.canvasStream.getAudioTracks().forEach((track) => {
      track.stop();
      this.data?.canvasStream.removeTrack(track);
    });

    this.inputStream.getAudioTracks().forEach((track) => {
      this.data?.canvasStream.addTrack(track);
    });

    this.data.video.srcObject = this.inputStream;
    this.data.video.play();
  }

  private draw() {
    if (!this.data) return;

    if (this.inputStream) {
      // redifining canvas size on every draw
      // to handle resolution change during screenshare
      const videoTrack = this.inputStream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      this.data.canvas.width = settings.width || 1280;
      this.data.canvas.height = settings.height || 720;
    }

    this.data.renderingCtx.drawImage(
      this.data.video, 
      0, 
      0, 
      this.data.canvas.width, 
      this.data.canvas.height,
    );        
  }

  start() {
    if (!this.data) {
      console.error("attempting to start canvas wrapper before init");
      return;
    }

    if (this.backgroundTask) {
      console.error("attempting to start canvas wrapper while already started");
      return;
    }

    const draw = this.draw.bind(this);
    this.backgroundTask = setWorkerInterval(draw, 1000 / SCREENSHARE_FPS);
  }

  stop() {
    this.backgroundTask?.clear();
    this.backgroundTask = undefined;
  }

  cleanup() {
    if (this.data) {
      this.data.canvasStream.getTracks().forEach((track) => track.stop());
      this.data = undefined;
    }
    if (this.inputStream) {
      this.inputStream.getTracks().forEach((track) => track.stop());
      this.inputStream = undefined;
    }
    this.stop();
  }
}

export const ScreenShare = forwardRef(({ meetingContainerAPI, onStatus, onStream }: ScreenShareProps, ref: ForwardedRef<ScreenShareImperativeAPI>) => {
  const [session] = useMeetingSelector(meetingContainerAPI, state => state.session);

  const [status, setStatus] = useState<ScreenShareStatus>("idle");
  const silentAudioTrack = useRef<MediaStreamSilentTrack>();

  const dynamicMediaAPI = useDynamicMedia(meetingContainerAPI, {
    onStreamStart: (_, success) => {
      if (!success && status === "starting") {
        error("could not set media stream");
        cleanup();
        return;
      }
      setStatus("ongoing");
    },
  });
  const dynamicMediaAPIRef = useRefAutoUpdate(dynamicMediaAPI);

  const wrapperRef = useRef<CanvasWrapper>();

  const interactionAvailable = status === "idle" || status === "ongoing";

  const cleanup = async () => {
    const currentStream = dynamicMediaAPIRef.current.get(ScreenMediaName);
    if (currentStream) {
      setStatus("stopping");
      currentStream.getTracks().forEach((track) => track.stop());
    }

    if (silentAudioTrack.current) {
      silentAudioTrack.current.destroy();
      silentAudioTrack.current = undefined;
    }

    if (wrapperRef.current) {
      wrapperRef.current.cleanup();
      wrapperRef.current = undefined;
    }

    setStatus("idle");
  };

  const stop = async () => {
    try {
      cleanup();
      dynamicMediaAPIRef.current.remove(ScreenMediaName);
    } catch(err) {
      console.error(err);
    }
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        stop
      };
    },
    []
  );

  useEffect(() => {
    onStatus?.(status);
  }, [status, onStatus]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  const error = (detail: string, error?: any) => {
    toastError("Le partage d'écran a échoué.");
    console.error(`screenshare failed : ${detail}${error ? `\n${error}` : ""}`);
  };

  const onStartShare = async () => {
    if (!interactionAvailable) return;
    if (dynamicMediaAPI.get(ScreenMediaName)) return stop();

    if (!session) return;
    const updater = meetingContainerAPI?.updater;
    if (!updater) return;

    let originalStream: MediaStream;
    let stream: MediaStream;
    let wrapper: CanvasWrapper;

    try {
      wrapper = new CanvasWrapper();
      const info = wrapper.init();
      wrapper.start();
      
      originalStream = await navigator.mediaDevices.getDisplayMedia(DISPLAY_MEDIA_CONSTRAINTS);
      wrapper.setInputStream(originalStream);
      
      stream = info.canvasStream;
      if (!stream.getAudioTracks().length) {
        const silentTrack = new MediaStreamSilentTrack(getAudioContextForVolumeAnalyzer());
        silentAudioTrack.current = silentTrack;
        stream.addTrack(silentTrack.track);
      }

      const videoTrack = originalStream.getVideoTracks()[0];
      // warning : 'onStopShare' here will be frozen, important to use refs inside
      if (videoTrack) videoTrack.onended = stop;
    } catch {
      return;
    }

    const success = await dynamicMediaAPI.set(ScreenMediaName, stream, undefined);
    if (!success) {
      cleanup();
      return;
    }

    onStream?.(stream);
    //setStatus("starting");
    wrapperRef.current = wrapper; 
  };

  // TODO : needs a disabled props for when it is not rady (!interactionAvailable)
  return <ButtonSharedScreen isEnabled={!!dynamicMediaAPI.get(ScreenMediaName)} onClick={onStartShare} />;
});
